import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { api } from 'services/api';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  ModalFooter,
  Flex,
  Box,
  Text,
  Stack,
  useMediaQuery,
  Progress,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';
import {
  SUMMARY_CARD_BORDER_COLOR,
  SUMMARY_CARD_BORDER_RADIUS,
} from 'components/SummaryCard';

import { useSocket } from 'hooks/useSocket';

import { IntegrationImport } from 'types/import';
import { UnitCost } from 'types/unit-cost';

import ProgressMetricsCards from './ProgressMetricsCards';
import SectionStatusCard from './SectionStatusCard';
import StatusPill from './StatusPill';
import type { OverallStatus, ProgressSectionId, SectionStatus } from './types';

interface ModalUnitCostImportLookupProps extends Omit<ModalRootProps, 'data'> {
  data: {
    importId: number;
    unitCost: UnitCost;
  };
}

type ImportStatus = IntegrationImport['status'];
type SectionProgress = IntegrationImport['progress_bar'][ProgressSectionId];
type ProgressStatus = NonNullable<SectionProgress>['status'];

type StatusDescriptor = Pick<OverallStatus, 'label' | 'variant'>;
type SectionDescriptor = Pick<SectionStatus, 'statusLabel' | 'variant'>;

type ProgressSocketPayload = {
  task: string;
  progress: SectionProgress | null;
};

const DEFAULT_OVERALL_STATUS: StatusDescriptor = {
  label: 'Em processamento',
  variant: 'processing',
};

const IMPORT_STATUS_DETAILS: Record<ImportStatus, StatusDescriptor> = {
  created: { label: 'Não configurado', variant: 'waiting' },
  analyzing: { label: 'Analisando arquivo', variant: 'processing' },
  processing: { label: 'Processando dados', variant: 'processing' },
  storing: { label: 'Persistindo dados', variant: 'processing' },
  recalculating: { label: 'Recalculando', variant: 'processing' },
  indexing: { label: 'Indexando', variant: 'processing' },
  downloading: { label: 'Indexando', variant: 'processing' },
  success: { label: 'Concluído', variant: 'success' },
  failed: { label: 'Falha', variant: 'error' },
};

const PROGRESS_STATUS_DETAILS: Record<ProgressStatus, SectionDescriptor> = {
  pending: { statusLabel: 'Aguardando', variant: 'waiting' },
  running: { statusLabel: 'Em processamento', variant: 'processing' },
  done: { statusLabel: 'Concluído', variant: 'success' },
  failed: { statusLabel: 'Com erro', variant: 'error' },
};

const DEFAULT_SECTION_DESCRIPTOR: SectionDescriptor = {
  statusLabel: 'Aguardando',
  variant: 'waiting',
};

const SECTION_TITLES: Record<ProgressSectionId, string> = {
  inputs: 'Insumos',
  compositions: 'Serviços',
  fragments: 'Composições',
};

const SECTION_ORDER: ProgressSectionId[] = [
  'inputs',
  'compositions',
  'fragments',
];

const toNumber = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const isProgressSectionId = (value: string): value is ProgressSectionId =>
  SECTION_ORDER.includes(value as ProgressSectionId);

const shouldPollStatus = (status?: ImportStatus): boolean =>
  !status || (status !== 'success' && status !== 'failed');

const modalMargin = 120;
const modalMobileFix = 100;

const ModalUnitCostImportLookup: React.FC<ModalUnitCostImportLookupProps> = ({
  onConfirm,
  handleClose,
  data: { unitCost, importId },
  ...restProps
}) => {
  const [isMobile] = useMediaQuery('(max-width: 728px)');

  const [selectedSectionId, setSelectedSectionId] =
    useState<ProgressSectionId | null>(null);
  const [progressState, setProgressState] = useState<
    Partial<Record<ProgressSectionId, SectionProgress | null>>
  >({});

  const getImport = useCallback(async () => {
    const { data: responseData } = await api.get(`/import/${importId}`);
    return responseData.data as IntegrationImport;
  }, [importId]);

  const queryImport = useQuery(['IntegrationImport', importId], getImport, {
    refetchInterval: (data) => (shouldPollStatus(data?.status) ? 2000 : false),
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    enabled: !!importId,
  });

  const integrationImport = queryImport.data;

  useEffect(() => {
    if (!integrationImport?.progress_bar) {
      return;
    }

    setProgressState({
      inputs: integrationImport.progress_bar.inputs ?? null,
      compositions: integrationImport.progress_bar.compositions ?? null,
      fragments: integrationImport.progress_bar.fragments ?? null,
    });
  }, [integrationImport?.progress_bar]);

  useSocket({
    channel: `progress.integration_import.${importId}`,
    enabled: !!importId,
    callback: (payload: ProgressSocketPayload) => {
      const task = payload?.task;
      if (!task || !isProgressSectionId(task)) {
        return;
      }

      setProgressState((prev) => ({
        ...prev,
        [task]: payload.progress,
      }));

      if (
        payload.progress?.status === 'done' ||
        payload.progress?.status === 'failed'
      ) {
        queryImport.refetch();
      }
    },
  });

  const fallbackSectionId =
    SECTION_ORDER.find(
      (sectionId) =>
        progressState[sectionId] ??
        integrationImport?.progress_bar?.[sectionId],
    ) ?? SECTION_ORDER[0];
  const activeSectionId = selectedSectionId ?? fallbackSectionId;

  const overallStatus: OverallStatus = integrationImport
    ? (IMPORT_STATUS_DETAILS[integrationImport.status] ??
      DEFAULT_OVERALL_STATUS)
    : DEFAULT_OVERALL_STATUS;

  const sections: SectionStatus[] = SECTION_ORDER.map((sectionId) => {
    const sectionProgress =
      progressState[sectionId] ??
      integrationImport?.progress_bar?.[sectionId] ??
      null;
    const descriptor =
      (sectionProgress?.status &&
        PROGRESS_STATUS_DETAILS[sectionProgress.status]) ||
      DEFAULT_SECTION_DESCRIPTOR;

    return {
      id: sectionId,
      title: SECTION_TITLES[sectionId],
      statusLabel: descriptor.statusLabel,
      variant: descriptor.variant,
      progress: sectionProgress,
    };
  });

  const selectedSection =
    sections.find((section) => section.id === activeSectionId) ?? sections[0];
  const selectedProgress = selectedSection?.progress ?? null;

  const progressValue = {
    processed: toNumber(selectedProgress?.progress),
    total: toNumber(selectedProgress?.max_steps),
    percent: toNumber(selectedProgress?.progress_percent),
  };

  const totalProcessed = progressValue.processed ?? 0;
  const totalItems = progressValue.total ?? 0;

  let calculatedPercentage = 0;
  if (progressValue.percent !== null) {
    calculatedPercentage = progressValue.percent;
  } else if (
    progressValue.processed !== null &&
    progressValue.total !== null &&
    progressValue.total > 0
  ) {
    calculatedPercentage =
      (progressValue.processed / progressValue.total) * 100;
  }

  const percentage = Math.min(Math.max(calculatedPercentage, 0), 100);
  const displayPercentage = Math.round(percentage);
  const progressLabel = selectedSection?.title ?? 'Itens';
  const isProgressRunning = selectedProgress?.status === 'running';
  const hasProgressData = !!selectedProgress;
  const progressDescription = hasProgressData
    ? 'Acompanhe o percentual geral da importação em tempo real.'
    : 'Aguardando dados do processamento.';
  const percentageLabel = hasProgressData ? `${displayPercentage}%` : '--';

  const handleConfirm = (): void => {
    if (onConfirm) onConfirm();
    handleClose();
  };

  return (
    <Modal {...restProps} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent
        sx={{
          width: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          maxWidth: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          height: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          maxHeight: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
        }}
      >
        <ModalHeader>Importação de Tabela do Sienge</ModalHeader>

        <ModalCloseButton />

        <ModalBody px={{ base: 6, md: 10 }} py={8}>
          <Stack spacing={{ base: 6, md: 8 }}>
            <Flex
              direction={{ base: 'column', md: 'row' }}
              justify="space-between"
              align={{ base: 'flex-start', md: 'center' }}
              gap={4}
            >
              <Box>
                {unitCost && (
                  <Stack spacing={1}>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      color="gray.500"
                      textTransform="uppercase"
                      letterSpacing="widest"
                    >
                      Tabela selecionada
                    </Text>
                    <Text fontSize="lg" fontWeight="semibold" color="gray.900">
                      {unitCost.external_id} - {unitCost.description}
                    </Text>
                  </Stack>
                )}
              </Box>

              <Stack spacing={1} align={{ base: 'flex-start', md: 'flex-end' }}>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="widest"
                >
                  Status geral
                </Text>
                <StatusPill {...overallStatus} />
              </Stack>
            </Flex>

            <Flex
              direction={{ base: 'column', lg: 'row' }}
              align={{ base: 'stretch', lg: 'flex-start' }}
              gap={{ base: 6, lg: 10 }}
            >
              <Stack
                spacing={4}
                minW={{ base: 'auto', lg: '280px' }}
                flexShrink={0}
              >
                {sections.map((section) => (
                  <SectionStatusCard
                    key={section.id}
                    {...section}
                    isActive={section.id === activeSectionId}
                    onSelect={setSelectedSectionId}
                  />
                ))}
              </Stack>

              <Flex direction="column" flex="1" gap={6}>
                <ProgressMetricsCards
                  metrics={[
                    {
                      label: `Total de ${progressLabel}`,
                      value: totalItems,
                    },
                    {
                      label: 'Total processado',
                      value: totalProcessed,
                    },
                  ]}
                />
                <Box
                  borderRadius={SUMMARY_CARD_BORDER_RADIUS}
                  borderWidth="1px"
                  borderColor={SUMMARY_CARD_BORDER_COLOR}
                  p={{ base: 6, md: 8 }}
                >
                  <Stack spacing={6}>
                    <Flex
                      direction={{ base: 'column', sm: 'row' }}
                      justify="space-between"
                      align={{ base: 'flex-start', sm: 'center' }}
                      gap={4}
                    >
                      <Stack spacing={1}>
                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          color="gray.500"
                          textTransform="uppercase"
                          letterSpacing="widest"
                        >
                          Progresso da importação
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          {progressDescription}
                        </Text>
                      </Stack>

                      <Stack
                        spacing={0}
                        align={{ base: 'flex-start', sm: 'flex-end' }}
                      >
                        <Text
                          fontSize="3xl"
                          fontWeight="bold"
                          color="brand.500"
                        >
                          {percentageLabel}
                        </Text>
                      </Stack>
                    </Flex>

                    <Stack spacing={3}>
                      <Progress
                        hasStripe={hasProgressData && isProgressRunning}
                        isAnimated={hasProgressData && isProgressRunning}
                        isIndeterminate={!hasProgressData}
                        h="16px"
                        value={hasProgressData ? displayPercentage : 0}
                        colorScheme={hasProgressData ? 'blue' : 'gray'}
                        opacity={hasProgressData ? 1 : 0.6}
                        borderRadius="3px"
                      />

                      {!hasProgressData && (
                        <Text fontSize="sm" color="gray.500">
                          Aguarde enquanto iniciamos o acompanhamento desta
                          etapa.
                        </Text>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              </Flex>
            </Flex>
          </Stack>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="blue" onClick={handleConfirm}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalUnitCostImportLookup;
